import { Route, Router, Switch } from 'react-router-dom';
import { History } from 'history';
import * as Sentry from '@sentry/react';
import MobileDetect from 'mobile-detect';
import { Provider } from 'react-redux';
import { compose, Store } from 'redux';
import Shell from './shared/ui-components/Shell/Shell';
import ScrollToTop from './shared/ui-components/ScrollToTop/ScrollToTop';
import StartseiteInteressent from './views/StartseiteInteressent/StartseiteInteressent';
import { generateNavigationEntries, updateHasSilos } from './store/Navigation.store';
import Startseite from './views/Startseite/Startseite';
import { RootState, store } from './configureStore';
import './App.module.scss';
import { Rollen } from './shared/types/enums';
import type { CurrentUser } from './shared/types';
import Keycloak from 'keycloak-js';
import {
    adminRouten,
    appRouten,
    createRoute,
    debitorRouten,
    farmleiterRouten,
    prospectRouten,
    RouteConfig,
    warenempfaengerRouten,
} from './routeDefinitions';
import Config from './shared/config';
import { ReactElement, useEffect, useMemo } from 'react';
import FlutterBridge from './flutter/flutterBridge';
import ChatInterface from './chat-bot/Chatbot';
import { PostHogFeature, PostHogProvider } from 'posthog-js/react';
import { posthog, PostHogConfig } from 'posthog-js';

const { isApp } = Config.getConfig();

declare global {
    interface Window {
        isMobile: string | null;
        store: Store<RootState>;
        __store__: Store<RootState>;
        __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: typeof compose;

        // Flutter_Channels
        DownloadChannel: MessagePort | undefined;
        LicenseChannel: MessagePort | undefined;
        NachrichtChannel: MessagePort | undefined;
        FrontendLifecycleChannel: MessagePort | undefined;
        TokenChannel: MessagePort | undefined;
        getToken: () => string | null;
    }
}

const md = new MobileDetect(window.navigator.userAgent);
window.isMobile = md.mobile();

window.getToken = () => {
    if (isApp()) {
        return window.sessionStorage.getItem('authentication');
    }
    return null;
};

type Props = {
    keycloak: Partial<Keycloak>;
    currentUser: CurrentUser;
    history: History<unknown>;
};

const SentryRoute = Sentry.withSentryRouting(Route);

function App(props: Props): ReactElement {
    const { currentUser, history } = props;
    const isAdmin = currentUser.roles.includes(Rollen.ADMIN);
    const isDebitor = currentUser.roles.includes(Rollen.DEBITOR);
    const isFarmleiter = currentUser.roles.includes(Rollen.FARMLEITER);
    const isWarenempfaenger = currentUser.roles.includes(Rollen.RECIPIENT);
    const isProspect = currentUser.roles.includes(Rollen.PROSPECT);

    const routeDefinitions = useMemo((): RouteConfig[] => {
        const routeDefinitions: RouteConfig[] = [];
        if (isApp() || isDebitor || isFarmleiter || isWarenempfaenger || isAdmin) {
            routeDefinitions.push(...appRouten);
        }

        if (isDebitor) {
            routeDefinitions.push(...debitorRouten);
        }

        if (isFarmleiter) {
            routeDefinitions.push(...farmleiterRouten);
        }

        if (isWarenempfaenger) {
            routeDefinitions.push(...warenempfaengerRouten);
        }

        if (isAdmin) {
            routeDefinitions.push(...adminRouten);
        }

        if (isProspect) {
            routeDefinitions.push(...prospectRouten, createRoute('/', StartseiteInteressent));
        } else {
            routeDefinitions.push(createRoute('/', Startseite));
        }

        return [...routeDefinitions];
    }, [isAdmin, isProspect, isDebitor, isWarenempfaenger, isFarmleiter]);

    useEffect(() => {
        // Generate navigation entries (hasSilos endpoint check removed)
        store.dispatch(generateNavigationEntries(currentUser.roles as Rollen[], store.getState()?.workflow?.currentWorkflowStep));
    }, []);

    useEffect(() => {
        const onPageLoad = () => {
            FlutterBridge.sendLifecycleState(true);
        };

        if (document.readyState === 'complete') {
            onPageLoad();
        } else {
            window.addEventListener('load', onPageLoad, false);
            return () => {
                window.removeEventListener('load', onPageLoad);
                FlutterBridge.sendLifecycleState(false);
            };
        }
        return () => {
            FlutterBridge.sendLifecycleState(false);
        };
    }, []);

    const options: Partial<PostHogConfig> = {
        api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
        autocapture: false,
        ip: false,
        capture_pageview: false,
        person_profiles: 'identified_only',
        capture_exceptions: false,
        capture_heatmaps: false,
        capture_pageleave: false,
        capture_performance: false,
        capture_dead_clicks: false,
    };

    const phClient = posthog.init(process.env.REACT_APP_PUBLIC_POSTHOG_KEY || '', options);
    phClient.identify(currentUser.email, { role: currentUser.roles[0], email: currentUser.email }, {});

    useEffect(() => {
        const syncSiloAccessFlag = () => {
            const hasSiloAccess = !!phClient.isFeatureEnabled('silos-access');
            store.dispatch(updateHasSilos(hasSiloAccess));
        };

        syncSiloAccessFlag();
        phClient.reloadFeatureFlags();
        const unsubscribe = phClient.onFeatureFlags(syncSiloAccessFlag);

        return () => {
            unsubscribe();
        };
    }, [phClient]);

    return (
        <Provider store={store}>
            <PostHogProvider client={phClient}>
                <Router history={history}>
                    <Shell keycloak={props.keycloak} currentUser={currentUser}>
                        <Switch>
                            {routeDefinitions.map((definition, index) => (
                                <SentryRoute key={index} path={definition.path} component={definition.component} />
                            ))}
                        </Switch>
                        <PostHogFeature flag={'chatbot-access'} match={true}>
                            <ChatInterface></ChatInterface>
                        </PostHogFeature>
                    </Shell>
                    {/*
                      Nach Shell mounten, damit History-Listener der Listen (useScrollRestoration)
                      VOR diesem Listener registriert werden. Bei PUSH schreibt dann zuerst die Liste
                      die echte scrollY in den Storage; ScrollToTop scrollt erst danach nach oben.
                    */}
                    <ScrollToTop />
                </Router>
            </PostHogProvider>
        </Provider>
    );
}

export default App;
